// De qué pantalla viene una pregunta, a partir de la ruta —
// ADR-2026-09-10-el-boton-flotante-de-preguntale-a-atlas.
//
// El botón flotante abre el chat desde CUALQUIER pantalla, así que no puede recibir la
// etiqueta a mano como `BotonPreguntaleAtlas` (que cada página escribe con lo que sabe).
// Acá se deduce de la ruta, con el mismo vocabulario que usa la interfaz: los modos
// toman su nombre de `COPY_MODOS`, el resto de una tabla corta. Lo que no se reconoce
// queda como "la app", y la ruta cruda viaja igual en `contexto.ruta`.
//
// Puro, para testearlo.

import { nombreCortoModo } from '../../pages/v2/modesCatalog';

/** Prefijos exactos → etiqueta. El orden importa: el más específico primero. */
const POR_PREFIJO: Array<[string, string]> = [
  // "Plantillas": el nombre de la interfaz de siempre; sólo el grupo C la ve como "Recrear"
  // (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva).
  ['/interfaz-v2/plantillas', 'Plantillas'],
  ['/interfaz-v2/coleccion/', 'una colección de plantillas'],
  ['/plantillas', 'Plantillas'],
  ['/coleccion/', 'una colección de plantillas'],
  ['/creaciones', 'Creaciones'],
  ['/resultados', 'Creaciones'],
  ['/redes-sociales/imagenes-generadas', 'Creaciones'],
  ['/redes-sociales/imagenes', 'Creaciones'],
  ['/lanzamientos/', 'la ficha de un lanzamiento'],
  ['/lanzamientos', 'Tienda online'],
  ['/instagram', 'el calendario de Instagram'],
  ['/mi-marca', 'Mi marca'],
  ['/editar-marca', 'Mi marca'],
  ['/editor-video/', 'un proyecto del Estudio de Video'],
  ['/editor-video', 'Estudio de Video'],
  ['/estudio-video', 'Estudio de Video'],
  ['/flujos', 'Flujos'],
  ['/flujo/', 'el editor de un flujo'],
  ['/estudio-marketing/mapa', 'Mapa del estudio de marketing'],
  ['/estudio-marketing/plan', 'Plan del estudio de marketing'],
  ['/estudio-marketing/marca', 'Marca del estudio de marketing'],
  ['/estudio-marketing/datos', 'Datos del estudio de marketing'],
  ['/estudio-marketing/insights', 'Insights del estudio de marketing'],
  ['/estudio-marketing/estrategia', 'Estrategia del estudio de marketing'],
  ['/estudio-marketing', 'Estudio de marketing'],
  ['/configuracion', 'Configuración'],
  ['/perfil', 'Perfil'],
  ['/subscription', 'Planes y suscripción'],
  ['/facturacion', 'Facturación'],
  ['/ayuda', 'Ayuda'],
  ['/animar-imagen', 'Animar imagen'],
  ['/carrusel', 'Carrusel de imágenes'],
  ['/shorts-studio', 'Shorts Studio'],
  ['/atajo', 'un atajo de imagen'],
  ['/exportar-tiendanube', 'Exportar a Tiendanube'],
  ['/diseno-de-indumentaria/', 'el lienzo de un diseño de indumentaria'],
  ['/diseno-de-indumentaria', 'Diseño de indumentaria'],
];

const INICIO = new Set(['/', '/inicio', '/interfaz-v2', '/generar']);

/** El segmento de modo de `/interfaz-v2/modo/<modo>` o `/modo/<modo>`, o null. */
function modoDeRuta(pathname: string): string | null {
  const m = /^(?:\/interfaz-v2)?\/modo\/([^/?#]+)/.exec(pathname);
  if (!m) return null;
  // Las variantes de banco de pruebas (`modelo-v2`) se nombran como su modo.
  return m[1].replace(/-v\d+$/, '');
}

const RE_PROYECTO_DE_VIDEO =
  /^\/editor-video\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i;

/**
 * El id del proyecto del Estudio de Video si la ruta es la de su editor; null si no. Lo
 * usa el chat para asociar la conversación al proyecto
 * (ADR-2026-09-11-el-chat-del-proyecto-de-video).
 */
export function proyectoDeRuta(pathname: string): string | null {
  const m = RE_PROYECTO_DE_VIDEO.exec(pathname || '');
  return m ? m[1].toLowerCase() : null;
}

/** Etiqueta humana de la pantalla en la que está el usuario. */
export function pantallaDeRuta(pathname: string): string {
  const ruta = (pathname || '/').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  if (INICIO.has(ruta)) return 'Inicio';
  const modo = modoDeRuta(ruta);
  if (modo) {
    const nombre = nombreCortoModo(modo, '');
    return nombre ? `el modo ${nombre}` : `el modo ${modo}`;
  }
  for (const [prefijo, etiqueta] of POR_PREFIJO) {
    if (ruta === prefijo || ruta.startsWith(prefijo)) return etiqueta;
  }
  return 'la app';
}

/**
 * Pantallas donde el flotante NO es opcional —
 * ADR-2026-09-15-en-un-lanzamiento-el-flotante-no-es-opcional.
 *
 * El botón de la esquina es una función avanzada apagada por defecto
 * (ADR-2026-09-10-el-flotante-del-chat-es-una-funcion-avanzada): el resto de las
 * pantallas abre el chat desde su propio botón en flujo. Estas NO tienen uno —a
 * pedido, para no gastar un control del encabezado— así que sin el flotante se
 * quedarían sin ninguna entrada al agente, que es distinto de "sin el atajo".
 *
 * Es una lista y no un booleano en cada pantalla porque el botón vive en el
 * CASCARÓN: una pantalla no puede montar un `fixed` propio —queda debajo de la
 * sidebar y del panel de contenido— así que la decisión tiene que viajar por lo
 * único que el cascarón conoce de la pantalla, que es su ruta.
 */
const SIEMPRE_CON_FLOTANTE = ['/lanzamientos/'];

export function elFlotanteVaSiempre(pathname: string): boolean {
  const ruta = (pathname || '/').split('?')[0].split('#')[0];
  // La pantalla de Tienda online (`/lanzamientos`, sin barra) también: ahí el agente
  // contesta sobre la tienda y sus métricas, y no tiene otra entrada a la vista
  // (ADR-2026-10-06-el-flotante-de-la-tienda-lleva-la-mascota).
  return ruta === '/lanzamientos' || SIEMPRE_CON_FLOTANTE.some((p) => ruta.startsWith(p));
}

/**
 * Tienda online y todo lo que cuelga de ella: ahí el flotante es el redondo con la
 * mascota y el degradé (ADR-2026-10-06-el-flotante-de-la-tienda-lleva-la-mascota).
 */
export function esTiendaOnline(pathname: string): boolean {
  const ruta = (pathname || '/').split('?')[0].split('#')[0];
  return ruta === '/lanzamientos' || ruta.startsWith('/lanzamientos/');
}

/**
 * Pantallas donde el flotante NO va aunque esté prendido —
 * ADR-2026-09-17-el-lienzo-del-diseno-no-lleva-flotante y
 * ADR-2026-09-22-el-estudio-y-el-lienzo-llaman-al-agente-por-su-nombre.
 *
 * El lienzo de un diseño tiene "Preguntále a Atlas" en su encabezado, y el flotante en
 * la esquina de abajo tapaba justo el rincón del lienzo donde caen las tandas nuevas:
 * dos entradas al agente, una de ellas encima del trabajo. La portada del modo
 * (`/diseno-de-indumentaria`, sin barra final) no tiene botón propio y lo conserva.
 * Lista por ruta por lo mismo que `SIEMPRE_CON_FLOTANTE`: el botón vive en el cascarón.
 *
 * El Estudio de Video es el mismo caso: desde que el Director se fundió en el agente
 * (ADR-2026-09-11) su botón abre este mismo chat, así que el flotante era una segunda
 * puerta a lo mismo, encima de la timeline. Sólo el editor de un proyecto
 * (`/editor-video/:id`); la lista de proyectos (`/editor-video`, sin barra final) no
 * tiene botón propio y lo conserva.
 */
const NUNCA_CON_FLOTANTE = ['/diseno-de-indumentaria/', '/editor-video/'];

/**
 * Pantallas donde el panel del chat NO ofrece "pantalla completa": en el modo Diseño el
 * agente acompaña al lienzo desde el costado, y expandirlo lo sacaría del diseño
 * (pedido de Matías del 2026-09-18). Todo el modo, portada incluida.
 */
const SIN_PANTALLA_COMPLETA = ['/diseno-de-indumentaria'];

export function sinPantallaCompleta(pathname: string): boolean {
  const ruta = (pathname || '/').split('?')[0].split('#')[0];
  return SIN_PANTALLA_COMPLETA.some((p) => ruta === p || ruta.startsWith(`${p}/`));
}

export function elFlotanteNoVa(pathname: string): boolean {
  const ruta = (pathname || '/').split('?')[0].split('#')[0];
  // No alcanza con `startsWith`: los prefijos terminan en barra y el router
  // acepta la barra sobrante, así que `/editor-video/` a secas es la LISTA de
  // proyectos, no el editor de uno. Esa pantalla no tiene botón propio, y
  // esconderle el flotante la dejaría sin ninguna entrada al agente, que es
  // justo lo que estas listas no quieren hacer. Por eso se exige que después
  // del prefijo venga algo: el id del proyecto o del diseño.
  return NUNCA_CON_FLOTANTE.some((p) => ruta.startsWith(p) && ruta.length > p.length);
}
